// src/pages/ReviewStatusPage.jsx
// PHASE 4.3 (Congressional Review System — Pulze-side read-only status
// mirror). For support staff with reviewConsoleAccess but no Operations
// Portal login. Zero action buttons, by design (4.3 spec) — matches
// AdminPage.jsx's established read-only-mirror convention (banner + "Open
// Operations Portal" link) but is deliberately its OWN standalone page, not
// a tab bolted onto AdminPage.jsx's existing shell.
//
// WHY STANDALONE, NOT AN ADMINPAGE.JSX TAB (the open decision the design
// doc flagged at 10.1, resolved here): AdminPage.jsx gates its ENTIRE page
// on role.isAdmin (from GET /api/admin/my-role) before rendering any tab.
// reviewConsoleAccess is a genuinely separate axis from isAdmin — a support
// staff member can have one without the other (see User.js's own field
// comments). Nesting this inside AdminPage.jsx would block a non-admin,
// review-access-only account at that page's own top-level gate before ever
// reaching a tab, defeating the entire point of this feature. This page
// does its own access check independently, by calling GET /api/review-status
// and handling a 403 directly — no dependency on role.isAdmin at all.
//
// Deliberately thin: per-type status counts + the most recent pending
// items' generic fields only (type, chamber, reason, created date). No
// trade details, no source links, no confirm/reject — matches
// routes/reviewStatus.js's own deliberately-scoped response shape.

import { useState, useEffect, useCallback } from 'react';
import { ClipboardList, AlertCircle, ExternalLink, Clock } from 'lucide-react';
import { API_URL } from '../styles/shared';

const OPS_PORTAL_URL = 'https://operations.marketpulze.com';

const STATUS_COLORS = {
  PENDING: '#f59e0b',
  CONFIRMED: '#00ff88',
  REJECTED: '#ef4444',
  IN_PROGRESS: '#00d4ff',
  RESOLVED: '#00ff88',
  WONT_FIX: '#64748b',
};
const statusColor = (status) => STATUS_COLORS[status] || '#64748b';

const TYPE_LABELS = { DATA_QUALITY: 'Data Quality', AMENDMENT: 'Amendments', CAPTURE_MANUAL: 'Manual Capture' };
const typeLabel = (t) => TYPE_LABELS[t] || t;

const fmtFull = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const authH = (token) => ({ Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' });

const Card = ({ children, style }) => (
  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '0.75rem', ...style }}>{children}</div>
);

export default function ReviewStatusPage({ user, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(''); // access-denied or error text — same slot, different meaning

  const load = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const r = await fetch(API_URL + '/api/review-status', { headers: authH(token) });
      const d = await r.json().catch(() => ({ success: false, message: 'Invalid response from server' }));
      if (r.status === 403) {
        setMessage('Your account does not have Review Console access.');
      } else if (!d.success) {
        setMessage(d.message || 'Could not load review status.');
      } else {
        setData(d.data);
      }
    } catch (e) {
      setMessage('Could not reach the server.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#64748b' }}>Loading...</div>;
  }

  if (message) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', color: '#ef4444' }}><AlertCircle size={40} /><p style={{ marginTop: '1rem' }}>{message}</p></div>
      </div>
    );
  }

  const summary = (data && data.summary) || [];
  const recentPending = (data && data.recentPending) || [];

  // Group flat summary rows by REVIEWTYPE for the stat cards — same visual
  // language as AdminPage.jsx's Requests tab (Pending/Approved/Rejected counts).
  const byType = {};
  summary.forEach((row) => {
    if (!byType[row.REVIEWTYPE]) byType[row.REVIEWTYPE] = {};
    byType[row.REVIEWTYPE][row.STATUS] = row.CNT;
  });

  return (
    <div style={{ padding: '2rem 1rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ClipboardList size={28} color="#00ff88" />
          <div>
            <h1 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 700 }}>Review Console Status</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.8rem' }}>MarketPulze · {user && user.email}</p>
          </div>
        </div>
        <button onClick={load}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '6px', color: '#00d4ff', cursor: 'pointer', fontSize: '0.8rem' }}>
          Refresh
        </button>
      </div>

      {/* Read-only banner — same convention as AdminPage.jsx's own */}
      <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <span style={{ color: '#f59e0b', fontSize: '0.82rem', fontWeight: 600 }}>
          📖 Read-only status — confirm, reject, and correction actions happen in the Operations Portal.
        </span>
        <a href={OPS_PORTAL_URL} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '6px', color: '#f59e0b', fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Open Operations Portal <ExternalLink size={12} />
        </a>
      </div>

      {/* Per-type status counts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {Object.keys(byType).length === 0 && (
          <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No review queue data available.</div>
        )}
        {Object.entries(byType).map(([type, statuses]) => (
          <Card key={type}>
            <p style={{ margin: '0 0 0.6rem', color: '#f1f5f9', fontWeight: 600, fontSize: '0.9rem' }}>{typeLabel(type)}</p>
            {Object.entries(statuses).map(([status, cnt]) => (
              <div key={status} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                <span style={{ color: statusColor(status), fontSize: '0.78rem', fontWeight: 600 }}>{status}</span>
                <span style={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: 700 }}>{cnt}</span>
              </div>
            ))}
          </Card>
        ))}
      </div>

      {/* Recent pending items — generic fields only, no trade join, no actions */}
      <p style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem' }}>Most recent pending items</p>
      {recentPending.length === 0
        ? <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Nothing pending right now.</div>
        : recentPending.map((row, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
              <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: 'rgba(0,212,255,0.12)', color: '#00d4ff' }}>{typeLabel(row.REVIEWTYPE)}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.78rem', textTransform: 'capitalize' }}>{row.CHAMBER}</span>
            </div>
            <p style={{ margin: '0 0 0.3rem', color: '#e2e8f0', fontSize: '0.85rem' }}>{row.REASON || '—'}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#475569', fontSize: '0.72rem' }}>
              <Clock size={11} /> {fmtFull(row.CREATEDAT)}
            </div>
          </Card>
        ))
      }
    </div>
  );
}
